'use client'

/**
 * ContactForm
 *
 * The interactive contact form (fields + hCaptcha + Airtable submit via the
 * `submitContactForm` server action + success modal + GTM tracking). Extracted
 * verbatim from the old Contact page so the lead-capture behavior is unchanged.
 * The two dropdown option lists are now props (driven by Sanity), with the
 * original lists as defaults.
 */
import type React from 'react'
import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ContactConfirmation } from '@/components/contact-confirmation'
import { Send, AlertCircle, Loader2 } from 'lucide-react'
import { submitContactForm } from '@/app/actions/contact'
import { trackFormSubmission, trackPageView, trackServiceInterest } from '@/lib/gtm'
import HCaptcha from '@hcaptcha/react-hcaptcha'

const DEFAULT_SERVICE_OPTIONS = ['Senior Portraits', 'Family Portraits', 'Engagement Session', 'Professional Headshots', 'Other/Not Sure']
const DEFAULT_REFERRAL_OPTIONS = ['Instagram', 'Friend/Family Referral', 'Google Search', 'Facebook', 'Through School', 'Other']

export function ContactForm({
  serviceOptions,
  referralOptions,
}: {
  serviceOptions?: string[]
  referralOptions?: string[]
}) {
  const services = serviceOptions?.length ? serviceOptions : DEFAULT_SERVICE_OPTIONS
  const referrals = referralOptions?.length ? referralOptions : DEFAULT_REFERRAL_OPTIONS

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', serviceType: '', message: '', hearAboutUs: '', newsletter: false })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [hCaptchaToken, setHCaptchaToken] = useState<string | null>(null)
  const [hCaptchaLoading, setHCaptchaLoading] = useState(true)
  const hCaptchaRef = useRef<HCaptcha>(null)

  useEffect(() => {
    trackPageView('Contact Page')
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hCaptchaToken) {
      setSubmitResult({ success: false, message: 'Please complete the security check before submitting.' })
      return
    }
    setIsSubmitting(true)
    setSubmitResult(null)
    trackFormSubmission('Contact Form', false)

    const formDataObj = new FormData()
    Object.entries(formData).forEach(([k, v]) => {
      if (k === 'newsletter') { if (v) formDataObj.append(k, 'on') } else { formDataObj.append(k, v as string) }
    })
    formDataObj.append('hCaptchaToken', hCaptchaToken)

    const result = await submitContactForm(formDataObj)
    setSubmitResult(result)
    setIsSubmitting(false)
    trackFormSubmission('Contact Form', result.success)
    if (result.success && formData.serviceType) trackServiceInterest(formData.serviceType)

    if (result.success) {
      setShowConfirmation(true)
      setFormData({ name: '', email: '', phone: '', serviceType: '', message: '', hearAboutUs: '', newsletter: false })
      setHCaptchaToken(null)
      hCaptchaRef.current?.resetCaptcha()
    }
  }

  const onChange = (field: string, value: string | boolean) => setFormData((p) => ({ ...p, [field]: value }))

  return (
    <>
      {showConfirmation && <ContactConfirmation onClose={() => setShowConfirmation(false)} />}
      <Card className="border-2 border-purple-100 shadow-lg">
        <CardContent className="p-8">
          {submitResult && !submitResult.success && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{submitResult.message}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Your Name *</Label>
                <Input id="name" type="text" placeholder="Enter your full name" value={formData.name} onChange={(e) => onChange('name', e.target.value)} required disabled={isSubmitting} className="border-gray-300 focus:border-purple-500 focus:ring-purple-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address *</Label>
                <Input id="email" type="email" placeholder="your.email@example.com" value={formData.email} onChange={(e) => onChange('email', e.target.value)} required disabled={isSubmitting} className="border-gray-300 focus:border-purple-500 focus:ring-purple-500" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
              <PhoneInput id="phone" placeholder="(734) 555-0123" value={formData.phone} onChange={(e) => onChange('phone', e.target.value)} disabled={isSubmitting} className="border-gray-300 focus:border-purple-500 focus:ring-purple-500" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">What are you interested in?</Label>
              <Select onValueChange={(v) => onChange('serviceType', v)} disabled={isSubmitting} value={formData.serviceType}>
                <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">How did you hear about me?</Label>
              <Select onValueChange={(v) => onChange('hearAboutUs', v)} disabled={isSubmitting} value={formData.hearAboutUs}>
                <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"><SelectValue placeholder="Select an option" /></SelectTrigger>
                <SelectContent>{referrals.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium text-gray-700">What questions do you have? Tell me what you'd like to know! *</Label>
              <Textarea id="message" placeholder="Ask me about pricing, availability, what to expect during a session, location ideas, or anything else you'd like to know..." value={formData.message} onChange={(e) => onChange('message', e.target.value)} required disabled={isSubmitting} rows={4} className="border-gray-300 focus:border-purple-500 focus:ring-purple-500" />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="newsletter" checked={formData.newsletter} onCheckedChange={(c) => onChange('newsletter', c as boolean)} disabled={isSubmitting} className="border-gray-300" />
              <Label htmlFor="newsletter" className="text-sm text-gray-600">I'd like to receive updates about special offers</Label>
            </div>
            <div className="flex flex-col items-center">
              {hCaptchaLoading && (
                <div className="flex items-center space-x-2 text-gray-500 mb-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading security check...</span>
                </div>
              )}
              <HCaptcha
                ref={hCaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY!}
                onVerify={(t) => setHCaptchaToken(t)}
                onExpire={() => setHCaptchaToken(null)}
                onError={() => setHCaptchaToken(null)}
                onLoad={() => setHCaptchaLoading(false)}
                theme="light"
                size="normal"
                tabIndex={0}
              />
            </div>
            <Button type="submit" disabled={isSubmitting || !hCaptchaToken} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-3 disabled:opacity-50">
              {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending Message...</>) : (<><Send className="w-4 h-4 mr-2" />Send Message</>)}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  )
}
